/**
 * La calificación del equipo, arriba en Overview (24-sep-2026).
 *
 * Propuesta A del lienzo https://claude.ai/artifact/RxgTiMr676yuASBVyayP1u,
 * elegida por Armando: el supervisor no recorre agente por agente; ve cómo va
 * el equipo y a quién atender hoy. Unos veinte números, los mismos con 13
 * agentes que con 40 — la regla de puntos crece en puntos, no en filas.
 *
 * Como el Inicio del ejecutivo, la pantalla NO decide umbrales: cada nota
 * llega con su señal calculada por el servidor (`ie.resumen_del_equipo`). Los
 * cortes llegan sólo para dibujar las zonas de la regla.
 *
 * Va con su PROPIO periodo (30 días hasta ayer) y no con la barra de filtros
 * de abajo: es la misma ventana que ve cada ejecutivo en su Inicio, y si se
 * moviera con la semana de la barra el supervisor vería otra nota que la del
 * agente al que va a darle coaching.
 *
 * Por la misma razón lleva los filtros del INICIO (28-sep-2026) y no los de la
 * barra: mismo componente, mismo significado y mismo default (Pública + Ventas
 * + No ventas). Con el mismo filtro, la fila de un agente dice lo que ve ese
 * agente en su Inicio. Son independientes de la barra de abajo: ni la mueven ni
 * se mueven con ella.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileDown } from 'lucide-react';

import { getInicioEquipo } from '../../api';
import { agentesFueraPorOmision } from '../../components/FiltroDeAgentes';
import { guardarFiltros, inicial } from '../../utils/filtrosPersistentes';
import {
  CHIP, FILTROS_INICIALES, TONO, TONO_SUAVE, corta, describirFiltros, f1, larga, rangos,
} from '../inicio/comun';
import Filtros from '../inicio/Filtros';
import ElegirAgentesPdf from './ElegirAgentesPdf';

// Su propia memoria, separada de la de la barra de abajo (que usa la ruta).
const CLAVE_FILTROS = 'overview:calificacion-del-equipo';

// Las mismas tres vistas que el Inicio del agente (pedido de los dueños del
// negocio, oct-2026). Abre en Ayer, igual que «En qué te estás equivocando».
// Qué días cuenta cada una lo decide el servidor (`ie.fechas_del_periodo`).
const PERIODOS = [['dia', 'Ayer'], ['semana', '7 días'], ['mes', '30 días']];

const SUPERFICIE = {
  background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14,
};
const ROTULO = {
  fontSize: 10.5, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#8b8b98',
};
const NEUTRO = '#6b7280';
const EQUIPO = '#a5b4fc';
const META = '#f5f5f7';
const ZONA = {
  detente: 'rgba(248,113,113,0.40)', revisa: 'rgba(251,191,36,0.40)', sigue: 'rgba(52,211,153,0.45)',
};
const COLOR = { detente: 'rojo', revisa: 'ámbar', sigue: 'verde' };

const pos = (v) => `${Math.max(0, Math.min(10, v)) * 10}%`;
const ancla = (v) => (v <= 1 ? '0%' : v >= 9 ? '-100%' : '-50%');

// Nombre corto para la etiqueta: nombre + inicial del apellido ("Carlos R."),
// para que dos "Carlos" no se lean igual.
const corto = (nombre) => {
  const p = String(nombre).trim().split(/\s+/);
  return p.length > 1 ? `${p[0]} ${p[1][0]}.` : p[0];
};

/** Reparte las etiquetas en renglones para que no se encimen: cada una va al
 *  primer renglón donde cabe; si no cabe en ninguno, al que menos se enciman.
 *  El ancho del texto se estima (≈6.6 px por carácter a 11.5 px): no hace
 *  falta medirlo exacto, sólo que dos vecinos no caigan en el mismo renglón. */
function renglones(items, ancho, maxRenglones = 4) {
  const fin = [];
  return items.map((it) => {
    const w = it.texto.length * 6.6 + 10;
    const x = (it.nota / 10) * ancho;
    const ini = Math.max(0, Math.min(ancho - w, x - w / 2));
    let fila = fin.findIndex((f) => f <= ini);
    if (fila === -1) {
      if (fin.length < maxRenglones) fila = fin.length;
      else fila = fin.indexOf(Math.min(...fin));
    }
    fin[fila] = ini + w;
    return { ...it, fila, izquierda: ini };
  });
}

/** Un punto por agente en la regla de 0 a 10, en la nota de su último día, y
 *  su nombre debajo, del color de su señal (Armando, 24-sep-2026: al principio
 *  sólo se escribían los de rojo y no se sabía quién era cada punto). Una
 *  rayita une cada nombre con su punto cuando el nombre baja de renglón. */
function Regla({ agentes, equipo, cortes, vista }) {
  const caja = useRef(null);
  const [ancho, setAncho] = useState(900);
  useEffect(() => {
    const el = caja.current;
    if (!el) return undefined;
    const medir = () => setAncho(el.clientWidth || 900);
    medir();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(medir) : null;
    if (ro) ro.observe(el);
    return () => ro && ro.disconnect();
  }, []);

  const conNota = agentes.filter((a) => a.periodo.nota != null)
    .sort((x, y) => x.periodo.nota - y.periodo.nota);
  const etiquetas = renglones(conNota.map((a) => ({
    a, nota: a.periodo.nota, texto: `${corto(a.agente)} ${f1(a.periodo.nota)}`,
  })), ancho);
  const filas = etiquetas.reduce((m, e) => Math.max(m, e.fila + 1), 0);
  const BARRA = 34;
  const RENGLON = 17;
  const PRIMERO = BARRA + 26;
  return (
    <div ref={caja} role="img"
         aria-label={`Nota del último día de ${conNota.length} agentes en una escala de 0 a 10: ${etiquetas.map((e) => e.texto).join(', ')}. Equipo ${f1(equipo)}, meta ${f1(cortes.meta)}.`}
         style={{ position: 'relative', height: PRIMERO + filas * RENGLON + 18 }}>
      <div style={{ position: 'absolute', left: 0, right: 0, top: BARRA, height: 14, borderRadius: 5,
                    overflow: 'hidden', display: 'flex' }}>
        <div style={{ width: `${cortes.detente * 10}%`, height: '100%', background: ZONA.detente }} />
        <div style={{ width: `${(cortes.sigue - cortes.detente) * 10}%`, height: '100%', background: ZONA.revisa }} />
        <div style={{ width: `${(10 - cortes.sigue) * 10}%`, height: '100%', background: ZONA.sigue }} />
      </div>
      {equipo != null && (
        <>
          <div style={{ position: 'absolute', top: BARRA - 12, height: 38, left: pos(equipo),
                        borderLeft: `2px dashed ${EQUIPO}` }} />
          <div style={{ position: 'absolute', top: 0, left: pos(equipo), transform: `translateX(${ancla(equipo)})`,
                        fontSize: 11.5, fontWeight: 600, color: EQUIPO, whiteSpace: 'nowrap' }}>
            Equipo {f1(equipo)}
          </div>
        </>
      )}
      {/* Si la meta cae a décimas del equipo, su etiqueta se encimaría: en ese
          caso sólo se dibuja la línea, y la meta ya se lee en la cifra de arriba. */}
      <div style={{ position: 'absolute', top: BARRA - 12, height: 38, left: pos(cortes.meta),
                    borderLeft: `2px solid ${META}` }} />
      {(equipo == null || Math.abs(cortes.meta - equipo) >= 1.2) && (
        <div style={{ position: 'absolute', top: 0, left: pos(cortes.meta), transform: `translateX(${ancla(cortes.meta)})`,
                      fontSize: 11.5, fontWeight: 700, color: META, whiteSpace: 'nowrap' }}>
          Meta {f1(cortes.meta)}
        </div>
      )}
      {etiquetas.filter((e) => e.fila > 0).map((e) => (
        <div key={`raya-${e.a.agente}`} aria-hidden="true"
             style={{ position: 'absolute', left: pos(e.nota), top: BARRA + 14, width: 1,
                      height: PRIMERO - BARRA - 14 + e.fila * RENGLON - 2,
                      background: 'rgba(255,255,255,0.18)' }} />
      ))}
      {conNota.map((a, i) => (
        <div key={a.agente}
             title={`${a.agente} · ${vista} ${f1(a.periodo.nota)} (${a.periodo.llamadas} llamadas) · 30 días ${f1(a.global.nota)}`}
             style={{ position: 'absolute', top: BARRA - 1, left: pos(a.periodo.nota),
                      width: 16, height: 16, marginLeft: -8, borderRadius: '50%', zIndex: 1 + (i % 2),
                      background: TONO[a.periodo.senal] || NEUTRO, border: '2px solid #0f0f13',
                      boxShadow: '0 0 0 1px rgba(255,255,255,.25)', boxSizing: 'border-box',
                      opacity: a.periodo.pocas_llamadas ? 0.55 : 1 }} />
      ))}
      {etiquetas.map((e) => (
        <div key={`nombre-${e.a.agente}`} title={e.a.agente}
             style={{ position: 'absolute', top: PRIMERO + e.fila * RENGLON, left: e.izquierda,
                      fontSize: 11.5, fontWeight: 600, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums',
                      color: TONO[e.a.periodo.senal] || '#9a9aa8' }}>
          {e.texto}
        </div>
      ))}
      {[...new Set([0, cortes.detente, cortes.sigue, 10])].map((v) => (
        <span key={v} style={{ position: 'absolute', bottom: 0, left: pos(v), transform: `translateX(${ancla(v)})`,
                               fontSize: 11, color: '#7a7a88', fontVariantNumeric: 'tabular-nums' }}>
          {Number.isInteger(v) ? v : f1(v)}
        </span>
      ))}
    </div>
  );
}

function Tendencia({ dias }) {
  return (
    <span aria-label={`Últimos ${dias.length} días: ${dias.map((d) => f1(d.nota)).join(', ')}`}
          style={{ display: 'inline-flex', gap: 3, alignItems: 'flex-end', height: 22 }}>
      {dias.map((d, i) => (
        <i key={d.fecha} title={`${corta(d.fecha)} · ${f1(d.nota)}`}
           style={{ display: 'block', width: 8, borderRadius: 2, height: Math.max(3, Math.round((d.nota / 10) * 22)),
                    background: TONO[d.senal] || NEUTRO, opacity: i === dias.length - 1 ? 1 : 0.55 }} />
      ))}
    </span>
  );
}

export default function CalificacionDelEquipo() {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [eligiendo, setEligiendo] = useState(false);
  // Sobreviven a la navegación y no a la recarga, como en el Inicio.
  const [filtros, setFiltrosEstado] = useState(() => inicial(CLAVE_FILTROS, 'filtros', FILTROS_INICIALES));
  const [periodo, setPeriodoEstado] = useState(() => inicial(CLAVE_FILTROS, 'periodo', 'dia'));
  // Se guardan JUNTOS: `guardarFiltros` sustituye lo guardado completo.
  const setFiltros = (f) => { guardarFiltros(CLAVE_FILTROS, { filtros: f, periodo }); setFiltrosEstado(f); };
  const setPeriodo = (v) => { guardarFiltros(CLAVE_FILTROS, { filtros, periodo: v }); setPeriodoEstado(v); };
  // Las categorías del desplegable se conservan entre cargas: sin esto, al
  // cambiar un filtro el desplegable desaparecería mientras llega la respuesta.
  const [categorias, setCategorias] = useState([]);

  useEffect(() => {
    let vigente = true;
    setError(null);
    // Sin Tanya, como la barra de abajo: aquí no hay desplegable de agentes,
    // así que la exclusión es fija (28-oct-2026, seguía saliendo en la tabla).
    getInicioEquipo(filtros, periodo, agentesFueraPorOmision())
      .then((r) => {
        if (!vigente) return;
        setDatos(r.data);
        setCategorias(r.data.categorias_disponibles || []);
      })
      .catch((e) => { if (vigente) setError(e.response?.data?.detail || 'No se pudo cargar la calificación del equipo.'); });
    return () => { vigente = false; };
  }, [filtros, periodo]);

  const filtrosUI = <Filtros valor={filtros} onCambio={setFiltros} categorias={categorias} />;

  if (error) {
    return (
      <div style={{ marginBottom: 20 }}>
        {filtrosUI}
        <div style={{ ...SUPERFICIE, padding: 16, marginTop: 12, color: TONO.revisa }}>{error}</div>
      </div>
    );
  }
  if (!datos) return <div className="loading">Cargando la calificación del equipo…</div>;

  const { cortes, agentes } = datos;
  // Todo lo de abajo sigue a la vista elegida; `equipo` y `conteo` son los del periodo.
  const equipo = datos.equipo_periodo;
  const conteo = datos.conteo_periodo;
  const rango = rangos(cortes);
  const anterior = datos.equipo_periodo_previo;
  const delta = equipo.nota != null && anterior != null ? equipo.nota - anterior : null;
  const ult = datos.ultimo_dia;
  const coaching = datos.coaching_sin_firmar;
  const vista = PERIODOS.find(([k]) => k === periodo)?.[1] || 'Ayer';
  // Cómo se nombra el periodo dentro de una frase.
  const delPeriodo = {
    dia: ult ? `del ${corta(ult)}` : 'del último día',
    semana: 'de sus últimos 7 días con llamadas',
    mes: `de los últimos ${datos.ventana.dias} días`,
  }[periodo];
  const comparadoCon = { dia: 'el día anterior', semana: 'los 7 días anteriores', mes: `los ${datos.ventana.dias} días anteriores` }[periodo];
  const selectorPeriodo = (
    <div role="group" aria-label="Periodo" style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
      {PERIODOS.map(([k, t]) => (
        <button key={k} type="button" aria-pressed={periodo === k}
                style={{ ...CHIP(periodo === k), padding: '7px 16px', fontSize: 13 }}
                onClick={() => setPeriodo(k)}>{t}</button>
      ))}
    </div>
  );

  return (
    <section aria-label="Calificación del equipo" style={{ marginBottom: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
                    flexWrap: 'wrap', marginBottom: 12 }}>
        <div>
          <h2 style={{ ...ROTULO, fontSize: 12, margin: 0 }}>Calificación del equipo · v2</h2>
          <span style={{ fontSize: 12, color: '#7a7a88' }}>
            {agentes.length} agentes con llamadas · últimos {datos.ventana.dias} días
            {ult ? `, calificado hasta el ${larga(ult)}` : ''} · {describirFiltros(filtros)}
            {' '}· no cambia con los filtros de abajo
          </span>
        </div>
        {/* Ayer / 7 días / 30 días junto al PDF, en el hueco de la derecha que
            marcaron los dueños del negocio. Juntos en un grupo: sueltos, el
            encabezado se parte en dos renglones y el PDF cae a la izquierda. */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginLeft: 'auto' }}>
          {selectorPeriodo}
          {agentes.length > 0 && (
            <button type="button" className="btn-secondary" onClick={() => setEligiendo(true)}>
              <FileDown size={14} /> Descargar PDF de agentes
            </button>
          )}
        </div>
      </div>

      <div style={{ marginBottom: 14 }}>{filtrosUI}</div>

      {agentes.length === 0 ? (
        <div style={{ ...SUPERFICIE, padding: '18px 20px', color: '#8b8b98' }}>
          {filtros.aprovechamiento === 'no_aprovechables'
            ? 'Las llamadas no aprovechables no llevan calificación: no hubo intercambio humano que evaluar.'
            : datos.filtros?.activos
              ? `Ninguna llamada calificada de los últimos ${datos.ventana.dias} días cumple los filtros.`
              : `Todavía no hay llamadas calificadas de ningún agente en los últimos ${datos.ventana.dias} días.`}
        </div>
      ) : (
        <>
          <div style={{ ...SUPERFICIE, padding: '18px 20px', display: 'grid', gap: 18 }}>
            <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))' }}>
              <div style={{ display: 'grid', gap: 4 }}>
                <span style={ROTULO}>Equipo · {periodo === 'dia' && ult ? corta(ult) : vista}</span>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <b style={{ fontSize: 36, fontWeight: 800, lineHeight: 1, color: TONO[equipo.senal] || '#e6e6ec' }}>{f1(equipo.nota)}</b>
                  <span style={{ color: '#8b8b98' }}>/ 10</span>
                </div>
                <span style={{ fontSize: 12.5, color: '#9a9aa8' }}>
                  Meta <b style={{ color: '#e6e6ec' }}>{f1(cortes.meta)}</b>
                  {equipo.falta_para_meta > 0 && <> · faltan <b style={{ color: '#e6e6ec' }}>{f1(equipo.falta_para_meta)}</b></>}
                  {delta != null && (
                    <> · <span style={{ color: delta >= 0 ? TONO.sigue : TONO.detente }}>
                      {delta >= 0 ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}</span> vs. {comparadoCon}</>
                  )}
                </span>
              </div>

              <div style={{ display: 'grid', gap: 6, gridColumn: 'span 2' }}>
                <span style={ROTULO}>{periodo === 'dia' ? `Cómo cerraron ${ult ? corta(ult) : 'el último día'}` : `Cómo van · ${vista}`}</span>
                <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
                  {['detente', 'revisa', 'sigue'].map((s) => (
                    <div key={s} style={{ borderRadius: 10, padding: '8px 12px', background: TONO_SUAVE[s],
                                          border: `1px solid ${TONO[s]}` }}>
                      <b style={{ fontSize: 24, color: TONO[s] }}>{conteo[s]}</b>{' '}
                      <span style={{ fontSize: 12.5 }}>en {COLOR[s]}</span>
                      <div style={{ fontSize: 11.5, color: '#9a9aa8' }}>{rango[s]}</div>
                    </div>
                  ))}
                </div>
                {datos.sin_llamadas_periodo > 0 && (
                  <span style={{ fontSize: 11.5, color: '#7a7a88' }}>
                    {datos.sin_llamadas_periodo} sin llamadas calificadas {periodo === 'dia' ? 'ese día' : 'en el periodo'}
                  </span>
                )}
              </div>

              <div style={{ display: 'grid', gap: 4 }}>
                <span style={ROTULO}>Coaching sin firmar</span>
                <b style={{ fontSize: 36, fontWeight: 800, lineHeight: 1 }}>{coaching.n}</b>
                <span style={{ fontSize: 12.5, color: '#9a9aa8' }}>
                  {coaching.n > 0 ? <>de {coaching.agentes} agente{coaching.agentes === 1 ? '' : 's'} · <Link to="/coaching" style={{ color: '#a5b4fc', textDecoration: 'none' }}>ver</Link></> : 'todo firmado'}
                </span>
              </div>
            </div>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#e6e6ec' }}>
                  Dónde cae cada agente · {periodo === 'dia' ? (ult ? corta(ult) : 'último día') : vista}
                </span>
                <span style={{ fontSize: 12, color: '#7a7a88' }}>Cada punto es un agente con su nota · pasa el cursor para ver el nombre completo</span>
              </div>
              <Regla agentes={agentes} equipo={equipo.nota} cortes={cortes} vista={vista.toLowerCase()} />
            </div>
          </div>

          <div style={{ display: 'grid', gap: 16, marginTop: 16, gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))' }}>
            <div style={{ ...SUPERFICIE, padding: '16px 18px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                <h3 style={{ margin: 0, fontSize: 15, color: '#e6e6ec' }}>Necesitan atención</h3>
                <span style={{ fontSize: 12, color: '#7a7a88' }}>Los {datos.atencion.length} más bajos {delPeriodo}</span>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
                <thead>
                  <tr style={{ ...ROTULO, color: '#7a7a88', textAlign: 'left' }}>
                    <th style={{ padding: '6px 4px', fontWeight: 700 }}>Agente</th>
                    <th style={{ padding: '6px 4px', fontWeight: 700 }}>{vista}</th>
                    <th style={{ padding: '6px 4px', fontWeight: 700 }}>30 días</th>
                    <th style={{ padding: '6px 4px', fontWeight: 700 }}>7 días</th>
                    <th style={{ padding: '6px 4px', fontWeight: 700 }}>Lo que más le cuesta</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.atencion.map((a) => (
                    <tr key={a.agente} style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                      <td style={{ padding: '8px 4px' }}>
                        <Link to={`/agents/${encodeURIComponent(a.agente)}/ver`}
                              style={{ color: '#e6e6ec', fontWeight: 600, textDecoration: 'none', display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                          <i style={{ width: 9, height: 9, borderRadius: '50%', background: TONO[a.periodo.senal] || NEUTRO, display: 'inline-block' }} />
                          {a.agente}
                        </Link>
                      </td>
                      <td style={{ padding: '8px 4px', fontWeight: 700, color: TONO[a.periodo.senal] || NEUTRO }}>
                        {f1(a.periodo.nota)}
                        {a.periodo.pocas_llamadas && (
                          <span title={`Sólo ${a.periodo.llamadas} llamadas en el periodo`} style={{ marginLeft: 6, fontSize: 10.5, color: TONO.revisa }}>pocas</span>
                        )}
                      </td>
                      <td style={{ padding: '8px 4px', color: '#c8c8d0' }}>{f1(a.global.nota)}</td>
                      <td style={{ padding: '8px 4px' }}><Tendencia dias={a.tendencia} /></td>
                      <td style={{ padding: '8px 4px', color: '#9a9aa8', fontSize: 12.5 }}>{a.criterio ? a.criterio.etiqueta : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Link to="/agents" style={{ display: 'inline-block', marginTop: 10, color: '#a5b4fc', fontSize: 12.5, fontWeight: 600, textDecoration: 'none' }}>
                Ver los {agentes.length} agentes →
              </Link>
            </div>

            <div style={{ ...SUPERFICIE, padding: '16px 18px', display: 'grid', gap: 12, alignContent: 'start' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <h3 style={{ margin: 0, fontSize: 15, color: '#e6e6ec' }}>Lo que más le cuesta al equipo</h3>
                <span style={{ fontSize: 12, color: '#7a7a88' }}>{vista}</span>
              </div>
              {/* El orden es por lo que le quita a la NOTA (peso × veces), no por
                  el porcentaje: por eso un 53% puede ir debajo de un 72%. */}
              {datos.criterios_equipo.criterios.length > 0 && (
                <span style={{ fontSize: 12, color: '#7a7a88', marginTop: -6 }}>Primero el que más baja la calificación.</span>
              )}
              {datos.criterios_equipo.criterios.length === 0 ? (
                <span style={{ color: TONO.sigue, fontSize: 13 }}>El equipo no falló ningún criterio con peso en la calificación.</span>
              ) : datos.criterios_equipo.criterios.map((c) => (
                <div key={c.clave} style={{ display: 'grid', gap: 5, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 13 }}>
                    <b style={{ fontWeight: 600, color: '#e6e6ec' }}>{c.etiqueta}</b>
                    <span style={{ color: '#c8c8d0', whiteSpace: 'nowrap' }}>{c.porcentaje}% lo cumple</span>
                  </div>
                  <div style={{ height: 8, borderRadius: 4, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${c.porcentaje}%`, background: TONO.revisa, borderRadius: 4 }} />
                  </div>
                  <span style={{ fontSize: 12, color: '#9a9aa8' }}>
                    No se hizo en {c.fallas} de {c.aplicables} llamadas
                    {c.agentes > 0 && <> · es lo que más le cuesta a {c.agentes} agente{c.agentes === 1 ? '' : 's'}</>}
                  </span>
                </div>
              ))}
              {datos.lo_que_hace_bien_el_equipo.length > 0 && (
                <div style={{ padding: '10px 12px', borderRadius: 10, background: 'rgba(52,211,153,0.07)',
                              border: '1px solid rgba(52,211,153,0.30)', fontSize: 12.5, lineHeight: 1.5, color: '#e6e6ec' }}>
                  <b style={{ color: TONO.sigue }}>Lo que el equipo hace bien:</b>{' '}
                  {datos.lo_que_hace_bien_el_equipo.map((c) => `${c.etiqueta.toLowerCase()} (${c.porcentaje}%)`).join(', ')}.
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {eligiendo && <ElegirAgentesPdf agentes={agentes} filtros={filtros} onCerrar={() => setEligiendo(false)} />}
    </section>
  );
}
